import { useEffect, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { AlertTriangle, Inbox, RotateCw } from 'lucide-react';

/**
 * The three non-data states every panel needs. One look for all of them so a
 * failing sync reads the same everywhere: a short sentence, the detail behind a
 * disclosure, and a Retry — never a raw JSON dump and never an endless spinner.
 */

export function EmptyState({
  title,
  description,
  action,
  compact = false,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={clsx('flex flex-col items-center justify-center text-center', compact ? 'py-6' : 'py-12')}>
      <Inbox className="h-5 w-5 text-text-muted" aria-hidden />
      <p className="mt-2 text-body-sm font-medium text-text-secondary">{title}</p>
      {description && <p className="mt-1 max-w-md text-body-xs text-text-tertiary">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** Pulls a readable message out of whatever a query threw. */
export function errorMessage(error: unknown): string {
  if (!error) return 'Something went wrong.';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

export function ErrorState({
  title = 'Couldn’t load this',
  error,
  details,
  onRetry,
  compact = false,
}: {
  title?: string;
  /** The thrown error; its message is shown under the title. */
  error?: unknown;
  /** Raw detail (API payloads, per-item failures) — hidden behind "Details". */
  details?: ReactNode;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const message = error ? errorMessage(error) : null;
  return (
    <div
      role="alert"
      className={clsx(
        'rounded-lg border border-error/20 bg-error-light/40 text-left',
        compact ? 'px-3 py-2' : 'px-4 py-3'
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-error" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-body-sm font-medium text-text-primary">{title}</p>
          {message && <p className="mt-0.5 text-body-xs text-text-secondary line-clamp-2">{message}</p>}
          {details && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-1 text-body-xs text-text-tertiary underline-offset-2 hover:underline"
            >
              {open ? 'Hide details' : 'Details'}
            </button>
          )}
          {open && details && (
            <div className="mt-2 max-h-48 overflow-auto rounded-md bg-surface-primary p-2 font-mono text-body-xs text-text-secondary break-words">
              {details}
            </div>
          )}
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="flex flex-none items-center gap-1 rounded-md border border-border-light bg-surface-primary px-3 py-1 text-body-xs font-medium text-text-secondary hover:text-text-primary"
          >
            <RotateCw className="h-3 w-3" aria-hidden /> Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Skeleton placeholder. After `slowAfterMs` it says so, so a slow backend is
 * visible instead of looking like a frozen page.
 */
export function LoadingState({
  rows = 3,
  label = 'Loading…',
  slowAfterMs = 12_000,
  height,
}: {
  rows?: number;
  label?: string;
  slowAfterMs?: number;
  /** Reserve this many px (e.g. the chart height) so the page doesn't jump when data lands. */
  height?: number;
}) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(t);
  }, [slowAfterMs]);

  return (
    <div className="space-y-3 py-2" aria-busy="true" aria-live="polite" style={height ? { minHeight: height } : undefined}>
      {height ? (
        <div className="animate-pulse rounded-md bg-surface-tertiary" style={{ height: height - 32 }} />
      ) : null}
      {!height && Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-4 animate-pulse rounded-md bg-surface-tertiary"
          style={{ width: `${90 - i * 15}%` }}
        />
      ))}
      <p className="text-body-xs text-text-tertiary">
        {slow ? 'Still loading — this source is slower than usual.' : label}
      </p>
    </div>
  );
}

/**
 * Renders the right state for a react-query result. Children render only once
 * data is present; `isEmpty` decides when to show the empty state instead.
 */
export function QueryState<T>({
  query,
  isEmpty,
  empty,
  errorTitle,
  loadingRows,
  children,
}: {
  query: { data: T | undefined; isLoading: boolean; error: unknown; refetch: () => unknown };
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  errorTitle?: string;
  loadingRows?: number;
  children: (data: T) => ReactNode;
}) {
  if (query.error) {
    return <ErrorState title={errorTitle} error={query.error} onRetry={() => query.refetch()} />;
  }
  if (query.isLoading || query.data === undefined) return <LoadingState rows={loadingRows} />;
  if (isEmpty?.(query.data)) return <>{empty ?? <EmptyState title="Nothing here yet" />}</>;
  return <>{children(query.data)}</>;
}
